import { z } from 'zod';
import { ActionType } from '../models/action-type.js';
/**
 * The action-node primitive and the outcome events it raises. Lives next to
 * form-node.ts for the same reason: the graph node is one row, but its
 * configuration + the events it emits are an entity large enough to own its
 * own file. `GRAPH_EVENT_REGISTRY` in graph.ts stitches these events in with
 * tool, runtime, and scratchpad events.
 */
/**
 * Events every action node emits regardless of its actionType. One fires per
 * `WorkflowAction` row completed (`action_executed`); one fires when the
 * whole `NodeActionGroup` finishes (`action_group_completed`).
 */
export const ACTION_BASE_EVENTS = [
    'action_executed',
    'action_group_completed',
];
/**
 * The action types the post-call phase sends. The API's dispatcher skips
 * every other type, so an action of another type reports no result.
 */
export const POST_CALL_ACTION_TYPES = new Set([
    ActionType.EMAIL,
    ActionType.SMS,
]);
/**
 * Events each action type emits in addition to the base events. Each action
 * type ships its outcomes with its own ticket; the list is empty until a
 * concrete action type is wired. TRANSFER lives on its own `transfer` node
 * (see transfer-node.ts) and does not appear here.
 */
export const ACTION_TYPE_EVENTS = {
    [ActionType.SMS]: [],
    [ActionType.EMAIL]: [],
    [ActionType.PHONE_CALL]: [],
    [ActionType.NOTIFICATION]: [],
    [ActionType.WEBHOOK]: [],
    [ActionType.TASK_CREATION]: [],
    [ActionType.PUSH_INTEGRATION]: [],
    [ActionType.TRANSFER]: [],
    [ActionType.SHARE_CONVERSATION]: [],
};
/**
 * An action node runs one logical action — a transfer, a notification, a
 * share-conversation — and routes on its outcome. The action's parameters
 * live on the referenced `NodeActionGroup` (named = shared across graphs;
 * nameless = inline, created and used by this one node). The graph itself
 * carries only the type and the reference; the group's content is loaded at
 * dispatch time by the runtime.
 */
export const actionConfigurationSchema = z.object({
    actionType: z.nativeEnum(ActionType),
    /**
     * The `NodeActionGroup` row this node delegates to. Empty string is
     * permitted so the author can drop an Action node and wire outcomes
     * before picking a group; the API's save gate catches an empty or
     * cross-tenant id and returns a focused issue the editor lands on.
     * Mirrors the pattern SUP-3147 established for transfer nodes.
     */
    nodeActionGroupId: z
        .string()
        .max(64)
        .refine(value => value === '' || /^c[a-z0-9]+$/.test(value), 'nodeActionGroupId must be a cuid or empty'),
});
/**
 * The action-node variant of a graph node. Joins the `graphNodeSchema` union
 * in graph.ts. Routing lives on `edges[]` via event-condition edges that key
 * on the base events + the per-type events declared in `ACTION_TYPE_EVENTS`.
 */
export const actionNodeSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(120),
    type: z.literal('action'),
    /** Display order within the graph. */
    position: z.number().int().nonnegative(),
    /** Author-visible layout; see nodeLayoutMetadataSchema in graph.ts. */
    layoutMetadata: z
        .object({ position: z.object({ x: z.number(), y: z.number() }) })
        .nullable()
        .optional(),
    ...actionConfigurationSchema.shape,
});
//# sourceMappingURL=action-node.js.map